import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/cn'

type Tone = 'neutral' | 'accent' | 'danger' | 'warning' | 'success'

const TONES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  accent: 'bg-accent/12 text-accent',
  danger: 'bg-danger/12 text-danger',
  warning: 'bg-warning/15 text-warning',
  success: 'bg-success/15 text-success',
}

export function Badge({
  tone = 'neutral',
  className,
  children,
  title,
}: {
  tone?: Tone
  className?: string
  children: ReactNode
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin text-muted-foreground', className)} />
}

export function Checkbox({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }) {
  const { indeterminate, ...rest } = props as { indeterminate?: boolean } & InputHTMLAttributes<HTMLInputElement>
  return (
    <input
      type="checkbox"
      ref={(node) => {
        if (node) node.indeterminate = Boolean(indeterminate)
      }}
      className={cn('h-4 w-4 cursor-pointer rounded border-border accent-[var(--accent)]', className)}
      {...rest}
    />
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-muted-foreground">{icon}</div>}
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {children}
    </div>
  )
}

export const ProgressBar = forwardRef<HTMLDivElement, { value: number; className?: string }>(
  function ProgressBar({ value, className }, ref) {
    const clamped = Math.max(0, Math.min(100, value))
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
      >
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-200"
          style={{ width: `${clamped}%` }}
        />
      </div>
    )
  },
)
